import {analyseAudio,refineRange} from './acousticBoundaries.js';
import {removeSourceRange, restoreSourceRange} from './sequence.js';
import {isRemoved} from './editModel.js';

const MAX_SHIFT = 0.16;
const WINDOW_SECONDS = 0.015;

// Use channel power (not a mono sum, which could cancel opposite-phase audio).
// Each result is a shared boundary: deleting/restoring either neighbour agrees.
export function refineWordTimings(words, buffer) {
  const channels = Array.from({length: buffer.numberOfChannels}, (_, i) => buffer.getChannelData(i));
  const rate = buffer.sampleRate;
  const window = Math.max(1, Math.round(WINDOW_SECONDS * rate));
  const next = words.map(word => ({...word, rawStart: word.rawStart ?? word.start, rawEnd: word.rawEnd ?? word.end}));
  for (let i = 1; i < next.length; i++) {
    const left = next[i - 1], right = next[i];
    const gap = right.rawStart - left.rawEnd;
    // Preserve long pauses and ambiguous overlapping recognition intervals.
    if (gap < 0 || gap > 0.25) continue;
    const middle = (left.rawEnd + right.rawStart) / 2;
    const low = Math.max(0, middle - MAX_SHIFT, right.rawStart - MAX_SHIFT,
      (left.rawStart + left.rawEnd) / 2 + 0.01);
    const high = Math.min(buffer.duration, middle + MAX_SHIFT, left.rawEnd + MAX_SHIFT,
      (right.rawStart + right.rawEnd) / 2 - 0.01);
    const from = Math.ceil(low * rate), to = Math.floor(high * rate);
    if (to - from < window * 2) continue;
    const power = new Float64Array(to - from);
    for (const channel of channels) {
      for (let n = from; n < to; n++) power[n - from] += channel[n] ** 2 / channels.length;
    }
    let sum = 0;
    for (let n = 0; n < window; n++) sum += power[n];
    let min = sum, max = sum, best = 0;
    for (let n = window; n < power.length; n++) {
      sum += power[n] - power[n - window];
      const index = n - window + 1;
      // For equally quiet positions prefer the one closest to the original gap.
      if (sum < min - 1e-12 || (Math.abs(sum - min) <= 1e-12 &&
        Math.abs((from + index + window / 2) / rate - middle) < Math.abs((from + best + window / 2) / rate - middle))) {
        min = sum; best = index;
      }
      max = Math.max(max, sum);
    }
    // Require a distinct dip. Flat noise, silence and uncertain edges stay put.
    if (max / window < 1e-8 || min > max * 0.36 || best === 0 || best === power.length - window) continue;
    const boundary = (from + best + window / 2) / rate;
    left.end = boundary;
    right.start = boundary;
  }
  return next;
}

// Refine deletion edges only. Recognition timestamps and internal words remain untouched.
export function refineProjectTiming(project, buffer, {experimental=false}={}) {
  if(experimental){
    const audio=analyseAudio(buffer),records=[...(project.cutRefinements ?? [])];
    let next=project;
    for(const cut of project.cuts){
      const proposal=refineRange(project.words,cut,audio,project.duration);
      if(proposal.start===cut.start && proposal.end===cut.end)continue;
      next=removeSourceRange(next,proposal.start,proposal.end);
      records.push({sourceStart:cut.start,sourceEnd:cut.end,start:proposal.start,end:proposal.end,report:proposal.report});
    }
    return {...next,cutRefinements:records};
  }
  if (project.alignment === 'elevenlabs') return project;
  const candidates = refineWordTimings(project.words, buffer);
  const starts = new Map(), ends = new Map();
  project.words.forEach((word,i)=>{starts.set(word.start,i);ends.set(word.end,i);});
  const records = [...(project.cutRefinements ?? [])];
  let next=project;
  for(const cut of project.cuts){
    const first=starts.get(cut.start), last=ends.get(cut.end);
    // A manually trimmed edge is not a transcription boundary; leave it alone.
    if(first===undefined || last===undefined || first>last)continue;
    // Protect retained neighbours. A quiet dip inside the *previous* spoken
    // word must never cause its tail to be deleted (and likewise the next onset).
    const previous=project.words[first-1], following=project.words[last+1];
    const earliest=Number.isFinite(previous?.end)
      ? Math.min(cut.start,previous.end+0.03) : 0;
    const latest=Number.isFinite(following?.start)
      ? Math.max(cut.end,following.start-0.03) : project.duration;
    const start=Math.max(earliest,Math.min(cut.start,candidates[first].start));
    const end=Math.min(latest,Math.max(cut.end,candidates[last].end));
    if(start===cut.start && end===cut.end)continue;
    next=removeSourceRange(next,start,end);
    records.push({sourceStart:cut.start,sourceEnd:cut.end,start,end});
  }
  return {...next,cutRefinements:records};
}

export function restoreTranscriptRange(project, start, end) {
  const sourceStart=start, sourceEnd=end;
  for(const edge of project.cutRefinements ?? []){
    if(edge.sourceStart>=sourceStart && edge.sourceStart<sourceEnd)start=Math.min(start,edge.start);
    if(edge.sourceEnd<=sourceEnd && edge.sourceEnd>sourceStart)end=Math.max(end,edge.end);
  }
  // Restoring an expanded cut must not resurrect a different deleted word.
  const protectedWords=project.words.filter(word=>(word.end<=sourceStart || word.start>=sourceEnd) && isRemoved(word,project.cuts,project.duration));
  let next=restoreSourceRange(project,start,end);
  for(const word of protectedWords)next=removeSourceRange(next,word.start,word.end);
  const records=[];
  for(const edge of project.cutRefinements ?? []){
    for(const cut of next.cuts){
      const remaining=project.words.filter(word=>word.start>=edge.sourceStart && word.end<=edge.sourceEnd && word.start>=cut.start && word.end<=cut.end);
      if(!remaining.length)continue;
      records.push({sourceStart:remaining[0].start,sourceEnd:remaining.at(-1).end,
        start:Math.max(edge.start,cut.start),end:Math.min(edge.end,cut.end)});
    }
  }
  return {...next,cutRefinements:records};
}
